import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/services/api';
import type { FloorInfo, TableZoneInfo, ManagedTableInfo, FloorLayoutInfo } from '@/types/api';
import { Plus, Edit2, Trash2, Star, Layout, LayoutGrid } from 'lucide-react';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { getSyncStatusBadge } from '@/utils/syncStatus';
import { pendingSyncRefetchInterval } from '@/utils/pendingSyncPolling';
import FloorModal from './modals/FloorModal';
import ZoneModal from './modals/ZoneModal';
import TableModal from './modals/TableModal';
import LayoutModal from './modals/LayoutModal';

interface FloorsZonesTabProps {
  restaurantId: number;
}

export function FloorsZonesTab({ restaurantId }: FloorsZonesTabProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const canCreate = useHasPermission(PERM.FLOOR_CREATE);
  const canUpdate = useHasPermission(PERM.FLOOR_UPDATE);
  const canDelete = useHasPermission(PERM.FLOOR_DELETE);

  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [isFloorModalOpen, setIsFloorModalOpen] = useState(false);
  const [editingFloor, setEditingFloor] = useState<FloorInfo | null>(null);
  const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<TableZoneInfo | null>(null);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<ManagedTableInfo | null>(null);
  const [isLayoutModalOpen, setIsLayoutModalOpen] = useState(false);
  const [editingLayout, setEditingLayout] = useState<FloorLayoutInfo | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch floors
  const { data: floors = [], isLoading: floorsLoading } = useQuery({
    queryKey: ['floors', restaurantId],
    queryFn: () => apiClient.getFloors(restaurantId),
    enabled: !!restaurantId,
    // Poll while a row is still pending (e.g. just pushed from draft mode), so the
    // page stops showing "Pending Sync" without needing a manual refresh.
    refetchInterval: pendingSyncRefetchInterval(`floors:${restaurantId}`),
  });

  // Fetch zones for selected floor
  const { data: zones = [] } = useQuery({
    queryKey: ['zones', restaurantId, selectedFloorId],
    queryFn: () => apiClient.getZonesByFloor(restaurantId, selectedFloorId!),
    enabled: !!selectedFloorId,
    refetchInterval: pendingSyncRefetchInterval(`zones:${restaurantId}:${selectedFloorId}`),
  });

  // Fetch tables for selected floor
  const { data: tables = [] } = useQuery({
    queryKey: ['managed-tables', restaurantId, selectedFloorId],
    queryFn: () => apiClient.getManagedTablesByFloor(restaurantId, selectedFloorId!),
    enabled: !!selectedFloorId,
    refetchInterval: pendingSyncRefetchInterval(`managed-tables:${restaurantId}:${selectedFloorId}`),
  });

  // Fetch layouts for selected floor
  const { data: layouts = [] } = useQuery({
    queryKey: ['layouts', restaurantId, selectedFloorId],
    queryFn: () => apiClient.getLayouts(restaurantId, selectedFloorId!),
    enabled: !!selectedFloorId,
    refetchInterval: pendingSyncRefetchInterval(`layouts:${restaurantId}:${selectedFloorId}`),
  });

  // Fetch shifts for layout modal
  const { data: shifts = [] } = useQuery({
    queryKey: ['shifts', restaurantId],
    queryFn: () => apiClient.getShifts(restaurantId),
    enabled: !!restaurantId,
  });

  // Draft mode may have just staged (rather than sent) any of the deletes/edits
  // below — refresh the badge on this page's DraftModeBar rather than waiting
  // for its own poll tick.
  const invalidateDraftStatus = () => queryClient.invalidateQueries({ queryKey: ['draftStatus'] });

  // Floor delete mutation
  const deleteFloorMutation = useMutation({
    mutationFn: (floorId: number) => apiClient.deleteFloor(restaurantId, floorId),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
      invalidateDraftStatus();
      setSelectedFloorId(null);
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.message || 'Failed to delete floor');
    },
  });

  // Zone delete mutation
  const deleteZoneMutation = useMutation({
    mutationFn: (zoneId: number) => apiClient.deleteZone(restaurantId, zoneId),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['zones', restaurantId, selectedFloorId] });
      queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
      invalidateDraftStatus();
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.message || 'Failed to delete zone');
    },
  });

  // Table delete mutation
  const deleteTableMutation = useMutation({
    mutationFn: (tableId: number) => apiClient.deleteManagedTable(restaurantId, tableId),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['managed-tables', restaurantId, selectedFloorId] });
      queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
      invalidateDraftStatus();
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.message || 'Failed to delete table');
    },
  });

  // Layout delete mutation
  const deleteLayoutMutation = useMutation({
    mutationFn: (layoutId: number) => apiClient.deleteLayout(restaurantId, layoutId),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['layouts', restaurantId, selectedFloorId] });
      queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
      invalidateDraftStatus();
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.message || 'Failed to delete layout');
    },
  });

  const handleDeleteFloor = async (floorId: number) => {
    if (window.confirm('Are you sure you want to delete this floor? All zones and tables on this floor will also be deleted.')) {
      try {
        await deleteFloorMutation.mutateAsync(floorId);
      } catch {
        // surfaced via deleteFloorMutation's onError -> deleteError banner
      }
    }
  };

  const handleDeleteZone = async (zoneId: number) => {
    if (window.confirm('Are you sure you want to delete this zone?')) {
      try {
        await deleteZoneMutation.mutateAsync(zoneId);
      } catch {
        // surfaced via deleteZoneMutation's onError -> deleteError banner
      }
    }
  };

  const handleDeleteTable = async (tableId: number) => {
    if (window.confirm('Are you sure you want to delete this table?')) {
      try {
        await deleteTableMutation.mutateAsync(tableId);
      } catch {
        // surfaced via deleteTableMutation's onError -> deleteError banner
      }
    }
  };

  // Delayed refetch to pick up sync status changes after POS ACK.
  // Ideally the backend would push the sync-status change over the existing
  // WebSocket infrastructure instead of this fixed delay, but that's a
  // backend change - out of scope here. This at least stops the pending
  // refetch from firing (and touching state) after the component unmounts,
  // and avoids piling up redundant timers if several edits happen in a row.
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefetch = () => {
    if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current);
    refetchTimeoutRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['zones', restaurantId, selectedFloorId] });
      queryClient.invalidateQueries({ queryKey: ['managed-tables', restaurantId, selectedFloorId] });
      queryClient.invalidateQueries({ queryKey: ['layouts', restaurantId, selectedFloorId] });
      refetchTimeoutRef.current = null;
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current);
    };
  }, []);

  const handleFloorModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
    invalidateDraftStatus();
    setIsFloorModalOpen(false);
    setEditingFloor(null);
    scheduleRefetch();
  };

  const handleZoneModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['zones', restaurantId, selectedFloorId] });
    queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
    invalidateDraftStatus();
    setIsZoneModalOpen(false);
    setEditingZone(null);
    scheduleRefetch();
  };

  const handleTableModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['managed-tables', restaurantId, selectedFloorId] });
    queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
    invalidateDraftStatus();
    setIsTableModalOpen(false);
    setEditingTable(null);
    scheduleRefetch();
  };

  const handleDeleteLayout = async (layoutId: number) => {
    if (window.confirm('Are you sure you want to delete this layout?')) {
      try {
        await deleteLayoutMutation.mutateAsync(layoutId);
      } catch {
        // surfaced via deleteLayoutMutation's onError -> deleteError banner
      }
    }
  };

  const handleLayoutModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['layouts', restaurantId, selectedFloorId] });
    queryClient.invalidateQueries({ queryKey: ['floors', restaurantId] });
    invalidateDraftStatus();
    setIsLayoutModalOpen(false);
    setEditingLayout(null);
    scheduleRefetch();
  };

  if (floorsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading floors...</div>
      </div>
    );
  }

  return (
    <>
      {deleteError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-red-700 hover:text-red-900 font-medium ml-4">
            Dismiss
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Floors List */}
        <div className="lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Floors</h3>
            {canCreate && (
              <button
                onClick={() => {
                  setEditingFloor(null);
                  setIsFloorModalOpen(true);
                }}
                className="btn btn-primary h-10"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Floor
              </button>
            )}
          </div>

          {floors.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-gray-500">No floors configured yet.</p>
              <p className="text-sm text-gray-400 mt-1">
                Add a floor to start organizing your restaurant layout.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {floors.map((floor) => {
                const badge = getSyncStatusBadge(floor.syncStatus);
                const isSelected = selectedFloorId === floor.id;
                return (
                  <div
                    key={floor.id}
                    className={`card p-4 transition-all ${
                      isSelected ? 'ring-2 ring-primary-500' : 'hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => setSelectedFloorId(floor.id)}
                      >
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {floor.name}
                        </h4>
                        <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                          <p>Dimensions: {floor.width} x {floor.height}</p>
                          <p>
                            {floor.tableCount} {floor.tableCount === 1 ? 'table' : 'tables'},
                            {' '}{floor.zoneCount} {floor.zoneCount === 1 ? 'zone' : 'zones'}
                          </p>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-2 ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </div>
                      {/* Outside the clickable info area above, so these never need
                          stopPropagation to avoid also selecting the floor. */}
                      <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                        <button
                          onClick={() => navigate(`/restaurants/${restaurantId}/tables/layout/${floor.id}`)}
                          className="text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                          aria-label={`Edit layout for floor ${floor.name}`}
                          title="Edit floor layout"
                        >
                          <LayoutGrid className="h-4 w-4" />
                        </button>
                        {canUpdate && (
                          <button
                            onClick={() => {
                              setEditingFloor(floor);
                              setIsFloorModalOpen(true);
                            }}
                            className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                            aria-label={`Edit floor ${floor.name}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDeleteFloor(floor.id)}
                            className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                            aria-label={`Delete floor ${floor.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Panel - Zones & Tables */}
        {selectedFloorId && (
          <div className="lg:col-span-2 space-y-6">
            {/* Zones Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Zones
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({zones.length})
                  </span>
                </h3>
                {canCreate && (
                  <button
                    onClick={() => {
                      setEditingZone(null);
                      setIsZoneModalOpen(true);
                    }}
                    className="btn btn-primary h-10"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Zone
                  </button>
                )}
              </div>

              {zones.length === 0 ? (
                <div className="card text-center py-8">
                  <p className="text-gray-500">No zones on this floor yet.</p>
                </div>
              ) : (
                <div className="card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Color
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Enabled
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tables
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {zones.map((zone) => (
                          <tr key={zone.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {zone.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {zone.color ? (
                                <div className="flex items-center space-x-2">
                                  <div
                                    className="w-5 h-5 rounded border border-gray-300"
                                    style={{ backgroundColor: zone.color }}
                                  />
                                  <span>{zone.color}</span>
                                </div>
                              ) : (
                                <span className="text-gray-400">--</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  zone.enabled
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {zone.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {zone.tableCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end space-x-1">
                                {canUpdate && (
                                  <button
                                    onClick={() => {
                                      setEditingZone(zone);
                                      setIsZoneModalOpen(true);
                                    }}
                                    className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                    aria-label={`Edit zone ${zone.name}`}
                                  >
                                    <Edit2 className="h-5 w-5" />
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    onClick={() => handleDeleteZone(zone.id)}
                                    className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                    aria-label={`Delete zone ${zone.name}`}
                                  >
                                    <Trash2 className="h-5 w-5" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Tables Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Tables
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({tables.length})
                  </span>
                </h3>
                {canCreate && (
                  <button
                    onClick={() => {
                      setEditingTable(null);
                      setIsTableModalOpen(true);
                    }}
                    className="btn btn-primary h-10"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Table
                  </button>
                )}
              </div>

              {tables.length === 0 ? (
                <div className="card text-center py-8">
                  <p className="text-gray-500">No tables on this floor yet.</p>
                </div>
              ) : (
                <div className="card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Capacity
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Shape
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Zone
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Active
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sync Status
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tables.map((table) => {
                          const badge = getSyncStatusBadge(table.syncStatus);
                          return (
                            <tr key={table.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {table.name || `Table #${table.id}`}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {table.capacity}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {table.shape}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {table.zoneName || <span className="text-gray-400">--</span>}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    table.active
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {table.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                                >
                                  {badge.label}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right">
                                <div className="flex items-center justify-end space-x-1">
                                  {canUpdate && (
                                    <button
                                      onClick={() => {
                                        setEditingTable(table);
                                        setIsTableModalOpen(true);
                                      }}
                                      className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                      aria-label={`Edit table ${table.name || table.id}`}
                                    >
                                      <Edit2 className="h-5 w-5" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      onClick={() => handleDeleteTable(table.id)}
                                      className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                      aria-label={`Delete table ${table.name || table.id}`}
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Layouts Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  <Layout className="h-5 w-5 inline-block mr-1 -mt-0.5" />
                  Layouts
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({layouts.length})
                  </span>
                </h3>
                {canCreate && (
                  <button
                    onClick={() => {
                      setEditingLayout(null);
                      setIsLayoutModalOpen(true);
                    }}
                    className="btn btn-primary h-10"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Layout
                  </button>
                )}
              </div>

              {layouts.length === 0 ? (
                <div className="card text-center py-8">
                  <p className="text-gray-500">No layouts on this floor yet.</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Add a layout to define table arrangements for this floor.
                  </p>
                </div>
              ) : (
                <div className="card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Shift
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Dimensions
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Positions
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {layouts.map((layout) => {
                          const syncBadge = getSyncStatusBadge(layout.syncStatus);
                          return (
                            <tr key={layout.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                <div className="flex items-center space-x-2">
                                  <span>{layout.name}</span>
                                  {layout.isDefault && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                                      <Star className="h-3 w-3 mr-1" />
                                      Default
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {layout.shiftName || 'All Shifts / Default'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {layout.designWidth} x {layout.designHeight}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {layout.positions.length}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${syncBadge.className}`}
                                >
                                  {syncBadge.label}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right">
                                <div className="flex items-center justify-end space-x-1">
                                  {canUpdate && (
                                    <button
                                      onClick={() => {
                                        setEditingLayout(layout);
                                        setIsLayoutModalOpen(true);
                                      }}
                                      className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                      aria-label={`Edit layout ${layout.name}`}
                                    >
                                      <Edit2 className="h-5 w-5" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      onClick={() => handleDeleteLayout(layout.id)}
                                      className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                      aria-label={`Delete layout ${layout.name}`}
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prompt to select a floor */}
        {!selectedFloorId && floors.length > 0 && (
          <div className="lg:col-span-2 flex items-center justify-center h-64">
            <p className="text-gray-500">Select a floor to manage its zones and tables.</p>
          </div>
        )}
      </div>

      {/* Floor Modal */}
      {isFloorModalOpen && (
        <FloorModal
          restaurantId={restaurantId}
          floor={editingFloor}
          onClose={() => {
            setIsFloorModalOpen(false);
            setEditingFloor(null);
          }}
          onSuccess={handleFloorModalSuccess}
        />
      )}

      {/* Zone Modal */}
      {isZoneModalOpen && selectedFloorId && (
        <ZoneModal
          restaurantId={restaurantId}
          floorId={selectedFloorId}
          zone={editingZone}
          onClose={() => {
            setIsZoneModalOpen(false);
            setEditingZone(null);
          }}
          onSuccess={handleZoneModalSuccess}
        />
      )}

      {/* Table Modal */}
      {isTableModalOpen && selectedFloorId && (
        <TableModal
          restaurantId={restaurantId}
          floorId={selectedFloorId}
          table={editingTable}
          zones={zones}
          onClose={() => {
            setIsTableModalOpen(false);
            setEditingTable(null);
          }}
          onSuccess={handleTableModalSuccess}
        />
      )}

      {/* Layout Modal */}
      {isLayoutModalOpen && selectedFloorId && (
        <LayoutModal
          restaurantId={restaurantId}
          floorId={selectedFloorId}
          layout={editingLayout}
          shifts={shifts}
          onClose={() => {
            setIsLayoutModalOpen(false);
            setEditingLayout(null);
          }}
          onSuccess={handleLayoutModalSuccess}
        />
      )}
    </>
  );
}

export default FloorsZonesTab;
